import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUserSearch } from '@/features/events/events.api';

export interface PickedUser {
  id: string;
  name: string;
  email: string;
}

/**
 * Find an account by name, email or phone and pick one. The same search the
 * membership-gift assign dialog uses; searching on Enter or the button rather
 * than per keystroke, because it hits the whole user table.
 */
export function UserPicker({
  value,
  onChange,
  enabled = true,
}: {
  value: PickedUser | null;
  onChange: (user: PickedUser | null) => void;
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const search = useUserSearch(term, enabled);
  const results = search.data?.results ?? [];

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border p-2">
        <div className="min-w-0 flex-1 text-sm">
          <p className="truncate font-medium">{value.name || '—'}</p>
          <p className="text-muted-foreground truncate text-xs">{value.email}</p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('vouchers.clearUser')}
          onClick={() => onChange(null)}
        >
          <X className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('vouchers.searchUser')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setTerm(draft);
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setTerm(draft)}
          disabled={!draft.trim() || search.isFetching}
          aria-label={t('common.search')}
        >
          {search.isFetching ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
        </Button>
      </div>

      {term && search.isSuccess && results.length === 0 && (
        <p className="text-muted-foreground text-xs">{t('vouchers.noUsers')}</p>
      )}

      {results.length > 0 && (
        <div className="max-h-48 divide-y overflow-y-auto rounded-lg border">
          {results.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => {
                onChange({
                  id: user.id,
                  name: [user.firstName, user.lastName].filter(Boolean).join(' '),
                  email: user.email,
                });
                setDraft('');
                setTerm('');
              }}
              className="hover:bg-accent w-full p-2 text-left text-sm transition-colors"
            >
              <p className="font-medium">
                {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
              </p>
              <p className="text-muted-foreground truncate text-xs">
                {user.email}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
